import React, { useState, useEffect } from "react";
import { Container, Form, Button, Card, Row, Col } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useJobContext } from "../../context/JobContext";
import { JobListDataProps, JobDetailItem } from "../detailjob/Section";

const JobEditor = () => {
    const { slug } = useParams<{ slug: string }>();
    const { jobs, addJob, updateJob } = useJobContext();
    const navigate = useNavigate();

    const [jobSlug, setJobSlug] = useState("");
    const [title, setTitle] = useState("");
    const [subTitle, setSubTitle] = useState("");
    const [cities, setCities] = useState("");
    const [items, setItems] = useState<JobDetailItem[]>([]);

    useEffect(() => {
        if (slug && jobs[slug]) {
            const job = jobs[slug];
            setJobSlug(slug);
            setTitle(job.title);
            setSubTitle(job.subTitle);
            setCities(job.cities || "");
            setItems(job.items);
        } else {
            // Initialize with one empty section
            setItems([
                {
                    upperTitle: "",
                    description: "",
                    listTitle: "",
                    list: [],
                    conclude: [],
                },
            ]);
        }
    }, [slug, jobs]);

    const handleItemChange = (
        index: number,
        field: keyof JobDetailItem,
        value: any
    ) => {
        const newItems = [...items];
        newItems[index] = { ...newItems[index], [field]: value };
        setItems(newItems);
    };

    const handleListChange = (
        itemIndex: number,
        listType: "list" | "conclude",
        value: string
    ) => {
        // Split by newline to create array
        const arrayValue = value.split("\n").filter((line) => line.trim() !== "");
        const newItems = [...items];
        newItems[itemIndex] = { ...newItems[itemIndex], [listType]: arrayValue };
        setItems(newItems);
    };

    const addItem = () => {
        setItems([
            ...items,
            {
                upperTitle: "",
                description: "",
                listTitle: "",
                list: [],
                conclude: [],
            },
        ]);
    };

    const removeItem = (index: number) => {
        const newItems = [...items];
        newItems.splice(index, 1);
        setItems(newItems);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const jobData: JobListDataProps = {
            title,
            subTitle,
            cities,
            items,
        };

        if (slug) {
            updateJob(slug, jobData);
        } else {
            // For new jobs, use the slug field (or generate one)
            // Here we assume user enters slug or we use title
            const finalSlug = jobSlug || title.toLowerCase().replace(/\s+/g, "-");
            addJob(finalSlug, jobData);
        }
        navigate("/cms");
    };

    return (
        <div className="page-content py-5">
            <Container>
                <h2 className="mb-4">{slug ? "Edit Job" : "Create New Job"}</h2>
                <Form onSubmit={handleSubmit}>
                    {!slug && (
                        <Form.Group className="mb-3">
                            <Form.Label>Slug (URL Identifier)</Form.Label>
                            <Form.Control
                                type="text"
                                value={jobSlug}
                                onChange={(e) => setJobSlug(e.target.value)}
                                placeholder="e.g. game-product-manager"
                                required
                            />
                        </Form.Group>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Job Title</Form.Label>
                        <Form.Control
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Sub Title</Form.Label>
                        <Form.Control
                            type="text"
                            value={subTitle}
                            onChange={(e) => setSubTitle(e.target.value)}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Cities</Form.Label>
                        <div className="d-flex gap-3 flex-wrap">
                            {["Danang", "Hanoi", "Milan", "Hochiminh"].map((city) => {
                                const currentCities = cities ? cities.split(",").map(c => c.trim()) : [];
                                return (
                                    <Form.Check
                                        key={city}
                                        type="checkbox"
                                        label={city}
                                        checked={currentCities.includes(city)}
                                        onChange={(e) => {
                                            const isChecked = e.target.checked;
                                            let newCities = [...currentCities];
                                            if (isChecked) {
                                                newCities.push(city);
                                            } else {
                                                newCities = newCities.filter((c) => c !== city);
                                            }
                                            setCities(newCities.join(", "));
                                        }}
                                    />
                                );
                            })}
                        </div>
                    </Form.Group>

                    <hr />
                    <h4>Sections</h4>
                    {items.map((item, idx) => (
                        <Card key={idx} className="mb-4 shadow-sm">
                            <Card.Body>
                                <div className="d-flex justify-content-between mb-3">
                                    <h5>Section {idx + 1}</h5>
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={() => removeItem(idx)}
                                    >
                                        Remove Section
                                    </Button>
                                </div>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Upper Title</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={item.upperTitle || ""}
                                                onChange={(e) =>
                                                    handleItemChange(idx, "upperTitle", e.target.value)
                                                }
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>List Title</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={item.listTitle || ""}
                                                onChange={(e) =>
                                                    handleItemChange(idx, "listTitle", e.target.value)
                                                }
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label>Description</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={item.description || ""}
                                        onChange={(e) =>
                                            handleItemChange(idx, "description", e.target.value)
                                        }
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>List Items (One per line)</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        value={item.list?.join("\n") || ""}
                                        onChange={(e) => handleListChange(idx, "list", e.target.value)}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Conclude Items (One per line)</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        value={item.conclude?.join("\n") || ""}
                                        onChange={(e) =>
                                            handleListChange(idx, "conclude", e.target.value)
                                        }
                                    />
                                </Form.Group>
                            </Card.Body>
                        </Card>
                    ))}

                    <Button variant="secondary" className="mb-4 me-2" onClick={addItem}>
                        Add Section
                    </Button>

                    <div className="d-flex gap-2">
                        <Button variant="primary" type="submit">
                            Save Job
                        </Button>
                        <Button variant="light" onClick={() => navigate("/cms")}>
                            Cancel
                        </Button>
                    </div>
                </Form>
            </Container>
        </div>
    );
};

export default JobEditor;
